import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  savingsVaultCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { mutateIdempotent } from "@/core/api/mutate-idempotent";
import { logger } from "@/core/utils/func/logger";
import { savingsEndpoints } from "@/features/savings/api/endpoints";
import type {
  LockSavingsVaultRequestDto,
  SavingsVaultDto,
} from "@/features/savings/api/dto";

/**
 * Bloque le coffre jusqu'à `lockedUntil` (UTC) ou prolonge un blocage en
 * cours (`POST /SavingsVaults/{id}/lock`) — jamais raccourcir. Fenêtre
 * 1–1 825 jours (`Savings__MinLockDays` / `Savings__MaxLockDays`). Seul un
 * déblocage anticipé en double validation lève un blocage avant terme.
 *
 * Idempotency-Key OBLIGATOIRE (guide §4.7, clé persistée par empreinte du
 * corps). 422 : échéance hors bornes ou antérieure à l'échéance en cours,
 * coffre clos.
 */
export const useLockSavingsVault = (vaultId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["savings", "savingsVaults", "lock", vaultId],
    mutationFn: (body: LockSavingsVaultRequestDto) =>
      mutateIdempotent<SavingsVaultDto>({
        scopeId: `savings.savingsVaults.lock.${vaultId}`,
        url: savingsEndpoints.savingsVaults.lock(vaultId),
        body,
      }),
    onSuccess: () =>
      invalidateAfterMutation(queryClient, savingsVaultCacheKeys(vaultId)),
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useLockSavingsVault failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "savings" }
      );
    },
  });
};
